import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { searchAllCards, getSetCards, normalizeSetName } from '../services/pokemonTcgApi';
import CardGrid from '../components/CardGrid';
import { Search, Calendar, ArrowLeft, DollarSign } from 'lucide-react';
import { slugify } from '../utils/slugify';
import Breadcrumbs from '../components/Breadcrumbs';
import CollectionStats from '../components/CollectionStats';
import { useCollectionStore } from '../lib/collectionStore';
import { useProfileStore } from '../lib/supabase';
import { ENERGY_ICONS } from '../utils/constants';
import { motion } from 'framer-motion';
import { useCurrencyStore } from '../lib/currencyStore';
import { formatPrice } from '../utils/formatPrice';
import AddCompleteSetButton from '../components/AddCompleteSetButton';
import { getCardPrice } from '../utils/cardPricing';
import { Helmet } from 'react-helmet-async';

type PokemonType = 'All' | 'Colorless' | 'Darkness' | 'Dragon' | 'Fighting' | 'Fire' | 'Grass' | 'Lightning' | 'Metal' | 'Psychic' | 'Water';

interface SetPriceStats {
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  totalValue: number;
}

// Helper function to generate page title
const getPageTitle = (cards: Card[]) => {
  if (!cards.length) return 'Loading...';
  return `${cards[0]?.set.name} - ${cards[0]?.set.series} Series | PokeCardIndex`;
};

export default function SetPage() {
  const { series, setName } = useParams();
  const [cards, setCards] = useState<Card[]>([]);
  const [filteredCards, setFilteredCards] = useState<Card[]>([]);
  const location = useLocation();
  const [selectedType, setSelectedType] = useState<PokemonType>('All');
  const queryParams = new URLSearchParams(location.search);
  const typeFilter = queryParams.get('type');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [priceStats, setPriceStats] = useState<SetPriceStats | null>(null);
  const { currency } = useCurrencyStore();
  
  // Set initial type filter from URL if present
  useEffect(() => {
    if (typeFilter) {
      const validType = typeFilter as PokemonType;
      if (validType in ENERGY_ICONS) {
        setSelectedType(validType);
        setActiveFilters([validType]);
      }
    } else {
      setActiveFilters([]);
    }
  }, [typeFilter]);

  // Update filtered cards whenever cards or selectedType changes
  useEffect(() => {
    if (selectedType === 'All') {
      setFilteredCards(cards);
    } else {
      setFilteredCards(cards.filter(card => card.types?.includes(selectedType)));
    }

    // Calculate price statistics
    if (cards.length > 0) {
      const prices = cards
        .map(card => getCardPrice(card))
        .filter(price => price > 0);

      if (prices.length > 0) {
        const stats: SetPriceStats = {
          avgPrice: prices.reduce((a, b) => a + b, 0) / prices.length,
          minPrice: Math.min(...prices),
          maxPrice: Math.max(...prices),
          totalValue: prices.reduce((a, b) => a + b, 0)
        };
        setPriceStats(stats);
      }
    }
  }, [selectedType, cards]);

  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  const [error, setError] = useState<string | null>(null); 
  const previousSetRef = useRef<{
    setName: string | null;
    cards: Card[];
  }>({ setName: null, cards: [] });
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const { loadCollection } = useCollectionStore();
  const { isAuthenticated } = useProfileStore();
  const pageTitle = getPageTitle(filteredCards);

  // Update URL when type filter changes
  useEffect(() => {
    if (selectedType !== 'All') {
      navigate(`?type=${selectedType}`, { replace: true });
    } else {
      navigate('.', { replace: true });
    }
  }, [selectedType, navigate]);

  // Handle scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = window.scrollY;
      const progress = (currentProgress / totalScroll) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchCards = async () => {
      if (!setName) return;

      // Check if we're loading the same set
      if (previousSetRef.current.setName === setName) {
        console.log('Already loaded this set:', setName);
        return;
      }

      // Check if we're loading a different set or need to reload
      if (previousSetRef.current.setName === setName && previousSetRef.current.cards.length > 0) {
        console.log('Using cached cards for set:', setName);
        setCards(previousSetRef.current.cards);
        setFilteredCards(previousSetRef.current.cards);
        return;
      }
      
      // Update the previous set reference
      previousSetRef.current = { setName, cards: [] };
      
      // Reset state for new set
      setCards([]);
      setFilteredCards([]);
      setLoadingProgress(0);

      setLoading(true);
      setError(null);

      try {
        // Load collection data if user is authenticated
        if (isAuthenticated) {
          await loadCollection();
        }
        
        if (!setName) {
          setError('Invalid set name');
          return;
        }

        // Clean and normalize the set name
        const decodedSetName = decodeURIComponent(setName)
          .replace(/-/g, ' ')
          .toLowerCase()
          .trim();
        
        // Handle trainer gallery cards
        const isTrainerGallery = decodedSetName.toLowerCase().includes('trainer gallery');
        let setId = normalizeSetName(decodedSetName);
        
        if (isTrainerGallery) {
          // Extract base set name and append TG suffix
          const baseSetName = decodedSetName.replace(/trainer gallery/i, '').trim();
          setId = `${normalizeSetName(baseSetName)}tg`;
        }

        if (!setId) {
          throw new Error(`Could not normalize set name: ${decodedSetName}`);
        }

        console.log('Using mapped set ID:', setId);
        setLoadingProgress(10);
        const fetchedCards = await getSetCards(setId);
        setLoadingProgress(90);

        if (fetchedCards && fetchedCards.length > 0) {
          setCards(fetchedCards);
          setFilteredCards(fetchedCards); // Initialize filtered cards
          previousSetRef.current.cards = fetchedCards;
          console.log(`Loaded ${fetchedCards.length} cards for set ${setId}`);
          setLoadingProgress(100);
        } else {
          setError('No cards found in this set');
        }
      } catch (error) {
        console.error('Error fetching set cards:', error);
        if (retryCount < MAX_RETRIES) {
          console.log(`Retrying... Attempt ${retryCount + 1} of ${MAX_RETRIES}`);
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, RETRY_DELAY);
        } else {
          setError(error instanceof Error ? error.message : 'Error loading cards');
          setCards([]);
          setFilteredCards([]);
        }
      } finally {
        setLoading(false);
        setLoadingProgress(0);
      }
    };

    fetchCards();
  }, [setName, isAuthenticated, loadCollection, retryCount]);

  const handleCardClick = (card: Card) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}/`);
  };

  const searchFilteredCards = filteredCards.filter(card => 
    card.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatReleaseDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back
            </Link>
            <h1 className="text-2xl font-bold">Error</h1>
          </div>
          <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
            {error}. Please try again later.
          </div>
        </div>
      </div>
    );
  }

  // Generate dynamic meta tags
  const setTitle = cards.length > 0 ? `${cards[0].set.name} - ${cards[0].set.series} Series | PokeCardIndex` : 'Loading Set...';
  const setDescription = cards.length > 0 ? 
    `Browse all ${cards.length} cards from the ${cards[0].set.name} set in the ${cards[0].set.series} series. View card prices, images, and detailed information. Released ${cards[0].set.releaseDate ? new Date(cards[0].set.releaseDate).getFullYear() : ''}.` :
    'Loading Pokémon card set...';
  const canonicalUrl = cards.length > 0 ? `https://pokecardindex.com/${slugify(cards[0].set.series)}/${slugify(cards[0].set.name)}` : '';

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{setTitle}</title>
        <meta name="description" content={setDescription} />
        <meta name="keywords" content={`${cards[0]?.set?.name || ''}, ${cards[0]?.set?.series || ''}, pokemon cards, card set, pokemon tcg, trading cards`} />
        <meta property="og:title" content={setTitle} />
        <meta property="og:description" content={setDescription} />
        <meta property="og:type" content="website" />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      </Helmet>
      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-green-500 origin-left z-50"
        style={{ scaleX: scrollProgress / 100 }}
      />

      <div className="bg-black border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-8 py-6">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <Link
                  to="/"
                  className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={20} />
                  Back
                </Link>
                <h1 className="text-2xl font-bold">{cards[0]?.set.name || 'Loading...'}</h1>
                {cards[0]?.set.releaseDate && (
                  <div className="flex items-center gap-2 text-white/60">
                    <Calendar size={16} />
                    <span className="text-sm">
                      {formatReleaseDate(cards[0].set.releaseDate)}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 mt-2">
                <p className="text-white/60">
                  {cards[0]?.set.series} · {cards.length} cards
                </p>
                {priceStats && (
                  <div className="flex items-center gap-2 text-white/80">
                    <DollarSign size={16} className="text-green-500" />
                    <span>Average Card Price: {formatPrice(priceStats.avgPrice, currency)}</span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/60">Set Value: {formatPrice(priceStats.totalValue, currency)}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Add Complete Set Button */}
            <div className="flex flex-col gap-4">
              {cards.length > 0 && (
                <AddCompleteSetButton 
                  cards={cards} 
                  setName={cards[0]?.set.name || 'Unknown Set'} 
                  className="self-start"
                />
              )}
              
              <div className="relative w-full md:w-96">
                <input
                  type="text"
                  placeholder="Search cards in this set..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white/5 text-white px-4 py-2 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/25 pl-10 placeholder-white/40"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
              </div>
            </div>
            
            {/* Active Filters */}
            {activeFilters.length > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-white/60 text-sm">Active Filters:</span>
                {activeFilters.map(filter => (
                  <div 
                    key={filter}
                    className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-sm"
                  >
                    <img 
                      src={ENERGY_ICONS[filter as keyof typeof ENERGY_ICONS]} 
                      alt={filter}
                      className="w-4 h-4"
                    />
                    <span>{filter}</span>
                    <button
                      onClick={() => {
                        setSelectedType('All');
                        setActiveFilters([]);
                        navigate('.', { replace: true });
                      }}
                      className="text-white/60 hover:text-white ml-1"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="max-w-[1600px] mx-auto px-8 py-4">
        <CollectionStats setId={cards[0]?.set.id} totalCards={cards.length} />
        {loading && loadingProgress > 0 && (
          <div className="mt-4">
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${loadingProgress}%` }}
              />
            </div>
            <p className="text-sm text-white/60 mt-2">Loading cards... {loadingProgress}%</p>
          </div>
        )}
      </div>

      {searchFilteredCards.length > 0 && (
        <div className="max-w-[1600px] mx-auto px-8 pb-8">
          <CardGrid 
            cards={searchFilteredCards} 
            activeFilter={selectedType}
            onCardClick={handleCardClick}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
}